/**
 * CodeExamples.tsx
 * Comprehensive examples of HeroUI Code component
 * 
 * Showcases all HeroUI Code features using our shared components
 */

import React from 'react';
import { PlaygroundPageLayout } from '../shared';
import { Code } from '@enfomentis/ui';
import { Card, CardBody, CardHeader } from '@enfomentis/ui';


export const CodeExamples: React.FC = () => {
  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        
        {/* Basic Usage */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Basic Usage</h3>
          <Card>
            <CardBody>
              <div className="space-y-4">
                <p className="text-text-primary">
                  Install the package using <Code>npm install @heroui/react</Code> or 
                  use yarn with <Code>yarn add @heroui/react</Code>.
                </p>
                <p className="text-text-primary">
                  You can also use inline code like <Code>console.log('Hello World')</Code> in sentences.
                </p>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Size Variations */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Size Variations</h3>
          <Card>
            <CardBody>
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-medium text-text-primary mb-3">Small (Default)</h4>
                  <p className="text-text-primary">
                    Use <Code size="sm">npm start</Code> to begin development.
                  </p>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-text-primary mb-3">Medium</h4>
                  <p className="text-text-primary">
                    Run <Code size="md">npm run build</Code> for production builds.
                  </p>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-text-primary mb-3">Large</h4>
                  <p className="text-text-primary">
                    Execute <Code size="lg">docker-compose up</Code> to start services.
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Color Variations */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Color Variations</h3>
          <Card>
            <CardBody>
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-medium text-text-primary mb-3">Default</h4>
                  <p className="text-text-primary">
                    Standard code styling: <Code color="default">git status</Code>
                  </p>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-text-primary mb-3">Primary</h4>
                  <p className="text-text-primary">
                    Important commands: <Code color="primary">git commit -m "message"</Code>
                  </p>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-text-primary mb-3">Secondary</h4>
                  <p className="text-text-primary">
                    Supporting code: <Code color="secondary">git branch</Code>
                  </p>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-text-primary mb-3">Success</h4>
                  <p className="text-text-primary">
                    Successful operations: <Code color="success">npm test -- --passWithNoTests</Code>
                  </p>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-text-primary mb-3">Warning</h4>
                  <p className="text-text-primary">
                    Caution required: <Code color="warning">rm -rf node_modules</Code>
                  </p>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-text-primary mb-3">Danger</h4>
                  <p className="text-text-primary">
                    Dangerous commands: <Code color="danger">sudo rm -rf /</Code>
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Radius Variations */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Border Radius</h3>
          <Card>
            <CardBody>
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-4">
                  <span className="text-text-primary">None:</span>
                  <Code radius="none">border-radius: 0</Code>
                </div>
                
                <div className="flex flex-wrap items-center gap-4">
                  <span className="text-text-primary">Small:</span>
                  <Code radius="sm">border-radius: small</Code>
                </div>
                
                <div className="flex flex-wrap items-center gap-4">
                  <span className="text-text-primary">Medium:</span>
                  <Code radius="md">border-radius: medium</Code>
                </div>
                
                <div className="flex flex-wrap items-center gap-4">
                  <span className="text-text-primary">Large:</span>
                  <Code radius="lg">border-radius: large</Code>
                </div>
                
                <div className="flex flex-wrap items-center gap-4">
                  <span className="text-text-primary">Full:</span>
                  <Code radius="full">border-radius: 9999px</Code>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Programming Examples */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Programming Context</h3>
          <div className="space-y-6">
            
            {/* React/TypeScript */}
            <Card>
              <CardHeader>
                <h4 className="text-lg font-semibold text-text-primary">React & TypeScript</h4>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  <p className="text-text-primary">
                    Define a state variable: <Code color="primary">const [count, setCount] = useState(0)</Code>
                  </p>
                  <p className="text-text-primary">
                    Type annotation: <Code color="secondary">interface User &#123; name: string; id: number &#125;</Code>
                  </p>
                  <p className="text-text-primary">
                    Hook dependency: <Code>useEffect(() =&gt; &#123;&#125;, [dependency])</Code>
                  </p>
                </div>
              </CardBody>
            </Card>

            {/* Terminal Commands */}
            <Card>
              <CardHeader>
                <h4 className="text-lg font-semibold text-text-primary">Terminal Commands</h4>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  <p className="text-text-primary">
                    Development server: <Code size="md">npm run dev</Code>
                  </p>
                  <p className="text-text-primary">
                    Type checking: <Code size="md">npx tsc --noEmit</Code>
                  </p>
                  <p className="text-text-primary">
                    Linting: <Code size="md">npx eslint src/ --ext .ts,.tsx</Code>
                  </p>
                </div>
              </CardBody>
            </Card>

            {/* Trading Journal Context */}
            <Card>
              <CardHeader>
                <h4 className="text-lg font-semibold text-text-primary">Trading Journal Context</h4>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  <p className="text-text-primary">
                    Calculate P&L: <Code color="success">totalPnL = (exitPrice - entryPrice) * quantity</Code>
                  </p>
                  <p className="text-text-primary">
                    Risk calculation: <Code color="warning">riskPerTrade = accountBalance * 0.02</Code>
                  </p>
                  <p className="text-text-primary">
                    Position size: <Code>positionSize = riskAmount / stopLossDistance</Code>
                  </p>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>

        {/* Documentation Style */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Documentation Examples</h3>
          <Card>
            <CardBody>
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-medium text-text-primary mb-3">Installation Instructions</h4>
                  <div className="space-y-2">
                    <p className="text-text-primary">1. Install dependencies: <Code>npm install</Code></p>
                    <p className="text-text-primary">2. Start development: <Code>npm run dev</Code></p>
                    <p className="text-text-primary">3. Open browser at: <Code color="primary">http://localhost:3000</Code></p>
                  </div>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-text-primary mb-3">Environment Setup</h4>
                  <div className="space-y-2">
                    <p className="text-text-primary">Set API endpoint: <Code>REACT_APP_API_URL=https://api.example.com</Code></p>
                    <p className="text-text-primary">Enable debug mode: <Code>DEBUG=true</Code></p>
                    <p className="text-text-primary">Database URL: <Code color="secondary">DATABASE_URL=sqlite:./data.db</Code></p>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Advanced Combinations */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Advanced Combinations</h3>
          <Card>
            <CardBody>
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-medium text-text-primary mb-3">Mixed Sizes & Colors</h4>
                  <p className="text-text-primary">
                    Execute <Code size="lg" color="primary">docker build -t myapp .</Code> then run 
                    <Code size="md" color="success">docker run -p 3000:3000 myapp</Code> and check 
                    <Code size="sm" color="secondary">docker ps</Code> for status.
                  </p>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-text-primary mb-3">Custom Styling</h4>
                  <p className="text-text-primary">
                    Custom class: <Code className="font-mono text-xs">Custom styled code</Code>
                  </p>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-text-primary mb-3">Error Scenarios</h4>
                  <div className="space-y-2">
                    <p className="text-text-primary">
                      ❌ Command failed: <Code color="danger">Error: Module not found</Code>
                    </p>
                    <p className="text-text-primary">
                      ⚠️ Deprecated: <Code color="warning">componentWillMount() is deprecated</Code>
                    </p>
                    <p className="text-text-primary">
                      ✅ Success: <Code color="success">Build completed successfully</Code>
                    </p>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

      </div>
    </PlaygroundPageLayout>
  );
};